/**
 * <park-admin-section> — the titled, rule-underlined section block that groups
 * every admin surface, lifted 1:1 from the `.yp-section`/`.yp-sechead` markup
 * that `components/admin/AdminLanding.astro` used to hand-roll
 * (anton/forrest#1000). The landing alone repeats it six times.
 *
 * It realises the "group related controls under sub-headers with dividers,
 * rather than a flat wall of ungrouped actions" half of admin-shell#FR-6 as a
 * reusable component instead of per-page markup.
 *
 * Single source of truth = the pure `renderParkAdminSection()` function below,
 * consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkAdminSection.astro`
 *     (server-side, inside <park-admin-section data-ssr>), and
 *   - the Claude-Design preview, where the element renders client-side.
 *
 * The section BODY is slotted, so this composes with <park-admin-tile-grid>,
 * <park-admin-list>, or any page-specific content.
 *
 * Attributes:
 *   - title="…"  section heading (h2).
 *   - hint="…"   optional right-aligned muted hint (omitted when empty).
 * Slotted content = the section body.
 *
 * Styling lives in wc/css/park-admin-section.css — no <style> here.
 *
 * @dsSpec design-system#FR-3 — the recurring operator section block.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-admin-section";

export interface ParkAdminSectionProps {
	title?: string;
	hint?: string;
}

/**
 * Pure render: (props, body HTML) → inner HTML string. No DOM access, so it is
 * safe server-side and client-side. `bodyHtml` is trusted author/server markup
 * (the slotted grid/list) and is emitted verbatim; the hint is omitted when empty.
 */
export function renderParkAdminSection(
	props: ParkAdminSectionProps,
	bodyHtml = "",
): string {
	const hint = props.hint
		? html`<span class="pa-hint">${esc(props.hint)}</span>`
		: "";
	return html`<section class="pa-section"><div class="pa-sechead"><h2>${esc(props.title ?? "")}</h2>${hint}</div>${bodyHtml}</section>`;
}

class ParkAdminSection extends ParkElement {
	static get observedAttributes(): string[] {
		return ["title", "hint"];
	}

	protected render(): string {
		return renderParkAdminSection(
			{ title: this.attr("title"), hint: this.attr("hint") },
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkAdminSection);

export { ParkAdminSection };
